<template>
  <div>
    <menu-content-container :border-bottom="false">
      <content-row
        v-if="supportsDynamicBlocks"
        style="display: block; justify-content: center"
        :margin-bottom="16"
        :margin-top="16"
      >
        <TTabsV2
          data-testid="mode-tabs"
          :options="modeOptionsNavigation"
          :defult-active-index="mode === 'static' ? 0 : 1"
          @on-tab-change="onModeTabChanged"
        ></TTabsV2>
      </content-row>
    </menu-content-container>
    <menu-content-container v-if="mode === 'dynamic'" border-full>
      <content-row>
        <TDropdown
          class="w-full"
          data-testid="MT-dropdown"
          :label="$t('api_block.select_feed')"
          :data="mergetags"
          :selected="
            forMergetag?.label || $t('api_block.select_feed_placeholder')
          "
          @selection-changed="selectForMergetag($event)"
        ></TDropdown>
      </content-row>
    </menu-content-container>
    <menu-content-container v-else border-full :header="pluralName">
      <SelectModal
        v-if="showSelection"
        :block-name="block.blockName"
        :selection="replaceIndex === null ? items : []"
        :replace-mode="replaceIndex !== null"
        @select="
          replaceIndex === null
            ? selectionUpdate($event)
            : replaceItemAt(replaceIndex, $event)
        "
        @close="closeSelection"
      />
      <t-note v-if="!isOnPrimaryFeedLang" class="mb-2">
        {{ $t("api_block.skeleton_locked_in_translation") }}
      </t-note>
      <VueDraggable
        v-model="items"
        class="mb-2 space-y-2"
        :handle="isOnPrimaryFeedLang ? '.drag-handle' : '.disable-drag-handle'"
      >
        <TTabsToggle
          v-for="(item, index) in items"
          :key="index"
          :header="item.name"
          show-remove
          show-replace
          data-testid="product-item"
          :replace-tooltip="$t('api_block.replace_item_action')"
          :remove-disabled="!isOnPrimaryFeedLang"
          @remove-block="removeItem(index as number)"
          @replace-block="openReplace(index as number)"
        >
          <menu-content-container
            :header="$t('forms.image_properties')"
            :padding-left-right="8"
            :margin-top="16"
          >
            <content-row>
              <ImageButtons
                :model-value="item.image"
                :index="index as number"
                show-replace
                @update:model-value="
                  updateItemField(index as number, 'image', $event)
                "
              />
            </content-row>
            <content-row>
              <TInput
                :model-value="item['image-alt'] || ''"
                :label="$t('forms.image_alt')"
                data-testid="image-alt"
                @update:model-value="
                  updateItemField(index as number, 'image-alt', $event)
                "
              />
            </content-row>
            <content-row>
              <TInput
                :model-value="item['image-title'] || ''"
                :label="$t('forms.image_title')"
                data-testid="image-title"
                @update:model-value="
                  updateItemField(index as number, 'image-title', $event)
                "
              />
            </content-row>
          </menu-content-container>
        </TTabsToggle>
      </VueDraggable>
      <TButton
        data-testid="add-more-items"
        :disabled="!isOnPrimaryFeedLang"
        @click="showSelection = true"
      >
        <template #icon>
          <PlusIcon />
        </template>
        {{ $t("api_block.add_items", { name: pluralName }) }}
      </TButton>
    </menu-content-container>

    <menu-content-container :header="$t('api_block.layout')">
      <content-row
        v-if="
          layout.find((item) => item.type === 'image')?.displayed === 'show'
        "
      >
        <TButtonGroup :label="$t('forms.block_layout')">
          <TGroupButton
            data="imgText"
            data-testid="image-text"
            small
            :active="itemsLayout === 'image-text'"
            @button-clicked="itemsLayout = 'image-text'"
          >
            <LayoutImgText />
          </TGroupButton>
          <TGroupButton
            data="column"
            data-testid="column"
            small
            :active="itemsLayout === 'column'"
            @button-clicked="itemsLayout = 'column'"
          >
            <LayoutOneColumn />
          </TGroupButton>
          <TGroupButton
            data="textImg"
            data-testid="text-image"
            small
            :active="itemsLayout === 'text-image'"
            @button-clicked="itemsLayout = 'text-image'"
          >
            <LayoutTextImg />
          </TGroupButton>
        </TButtonGroup>
        <TButtonGroup
          v-if="itemsLayout !== 'column' || maxPerRow > 1"
          :label="$t('forms.image_align')"
        >
          <TGroupButton
            data="top"
            data-testid="align-top"
            small
            :active="valign === 'top'"
            @button-clicked="valign = 'top'"
          >
            <AligmentTop />
          </TGroupButton>
          <TGroupButton
            data="middle"
            data-testid="align-middle"
            small
            :active="valign === 'middle'"
            @button-clicked="valign = 'middle'"
          >
            <AligmentMiddle />
          </TGroupButton>
          <TGroupButton
            data="bottom"
            data-testid="align-bottom"
            small
            :active="valign === 'bottom'"
            @button-clicked="valign = 'bottom'"
          >
            <AligmentBottom />
          </TGroupButton>
        </TButtonGroup>
      </content-row>
      <content-row v-if="itemsLayout === 'column'" class="!block">
        <TInputNumber
          v-model.number="maxPerRow"
          data-testid="loop-max-per-row-input"
          :label="$t('forms.max_per_row')"
          unit=""
          :min="1"
          :max="4"
          regex-pattern="[1-4]{1}"
        ></TInputNumber>
      </content-row>
      <content-row class="!mt-3">
        <t-switch
          v-model="stuckOnMobile"
          data-testid="do-not-stack-on-mobile-switch"
          :label="$t('forms.stuck')"
        ></t-switch>
      </content-row>
    </menu-content-container>
    <menu-content-container
      :header="$t('forms.column_properties')"
      :padding-left-right="8"
      :margin-top="15"
    >
      <TTabsToggle
        :header="`${$t('forms.column')}`"
        :data-testid="`column-${0}-tab`"
      >
        <template #icon>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4 5.25C3.15625 5.25 2.5 5.92969 2.5 6.75V14.25C2.5 15.0938 3.15625 15.75 4 15.75H16C16.8203 15.75 17.5 15.0938 17.5 14.25V6.75C17.5 5.92969 16.8203 5.25 16 5.25H4ZM7 6.75V14.25H4V6.75H7ZM8.5 6.75H11.5V14.25H8.5V6.75ZM16 6.75V14.25H13V6.75H16Z"
              fill="currentColor"
            />
          </svg>
        </template>
        <columnSettings :column-index="0" block-column></columnSettings>
      </TTabsToggle>
    </menu-content-container>

    <menu-content-container :border-bottom="false" :padding-left-right="8">
      <t-note>{{ $t("api_block.settings_layout_note") }}</t-note>
      <content-row>
        <VueDraggable
          key="product-blocks"
          :model-value="layout"
          :animation="150"
          group="apiSections"
          item-key="product-blocks"
          handle=".drag-handle"
          class="space-y-2"
          style="width: 100%"
          @update="onUpdate"
        >
          <TTabsToggle
            v-for="pblock in layout"
            :key="pblock.uid"
            :header="getLabel(pblock.type)"
            toggle-show
            :default-displayed="pblock.displayed === 'show'"
            :data-testid="`product-${pblock.type}-tab`"
            :lock="itemsLayout !== 'column' && pblock.type === 'image'"
            @showBlock="showBlock(pblock.type)"
            @hideBlock="hideBlock(pblock.type)"
          >
            <imageForm v-if="pblock.type === 'image'"></imageForm>
            <buttonForm v-else-if="pblock.type === 'button'"></buttonForm>
            <textForm
              v-else
              :name="pblock.type"
              :block="block"
              :selected-item="apiBlock.selectedItem.value"
              :layout="layout"
            ></textForm>
          </TTabsToggle>
        </VueDraggable>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full :margin-top="20" bor>
      <content-row :margin-bottom="20">
        <applyToAll
          v-if="!prodstructure"
          data-testid="apply-to-all"
          :label="applyToAllLabel"
          :block-name="typeName"
        ></applyToAll>
      </content-row>
    </menu-content-container>

    <menu-content-container :border-bottom="false" :margin-top="16">
      <content-row>
        <hide-on-mobile></hide-on-mobile>
      </content-row>

      <content-row :margin-top="16" :margin-bottom="20">
        <hide-on-desktop></hide-on-desktop>
      </content-row>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import applyToAll from "@/forms/elements/applyToAll.vue";
  import textForm from "./textForm.vue";
  import imageForm from "./imageForm.vue";
  import buttonForm from "./buttonForm.vue";
  import SelectModal from "./selectModal.vue";
  import hideOnMobile from "../components/hideOnMobile.vue";
  import hideOnDesktop from "../components/hideOnDesktop.vue";
  import columnSettings from "../elements/columnSettings.vue";
  import ImageButtons from "../components/ImageButtons/ImageButtons.vue";

  import { IData } from "types/AutoComplete/IData";
  import { useNotificationsStore } from "@/stores/notifications";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useApiBlocksStore } from "@/stores/apiBlocks";
  import useApiBlock from "@/composables/useApiBlock";

  import {
    LayoutImgText,
    LayoutOneColumn,
    LayoutTextImg,
    AligmentTop,
    AligmentMiddle,
    AligmentBottom,
    PlusIcon,
  } from "@topol/icons";
  import {
    TNote,
    TTabsV2,
    TInput,
    TInputNumber,
    TButtonGroup,
    TGroupButton,
    TTabsToggle,
    TButton,
    TDropdown,
    TSwitch,
  } from "@topol/twig-ui";
  import type { TDropdownOption } from "@topol/twig-ui";

  import { useDebounceFn } from "@vueuse/core";
  import { VueDraggable } from "vue-draggable-plus";
  import shortid from "shortid";
  import { useI18n } from "vue-i18n";
  import {
    computed,
    ref,
    onUpdated,
    onMounted,
    onBeforeUnmount,
    watch,
  } from "vue";

  interface FeedData extends IData {
    id: string;
    name: string;
  }

  interface ProductData extends IData {
    id: string;
    name: string;
  }

  const props = defineProps(["block"]);
  const { apiBlock } = useApiBlock(props.block.blockName);

  const uid = ref(null);
  const isInEuro = ref(false);
  const showSelection = ref(false);
  const replaceIndex = ref<number | null>(null);

  const notificationsStore = useNotificationsStore();
  const mjmlStore = useMjmlStore();
  const apiBlocksStore = useApiBlocksStore();
  const { t, te, locale } = useI18n();

  const typeName = computed(() => {
    return props.block.blockName;
  });

  const modeOptionsNavigation = computed(() => {
    interface ContentOptions {
      name: string;
      value: "static" | "dynamic";
      disabled?: boolean;
      tooltip?: string;
    }

    const options: ContentOptions[] = [
      {
        name: `${t("api_block.static")}`,
        value: "static",
        tooltip: "",
      },
      {
        name: `${t("api_block.dynamic")}`,
        value: "dynamic",
        tooltip: "",
      },
    ];

    return options;
  });

  const items = computed({
    get() {
      return mjmlStore.getActiveBlockFeedItems(props.block);
    },
    set(val: any) {
      // VueDraggable v-model: only reorders. We forward the new order
      // and let the store remap to the primary skeleton by id, so any
      // per-language overrides (keyed by id) survive the reorder.
      mjmlStore.reorderFeedItems({
        uid: props.block.uid,
        newOrder: val,
      });
    },
  });

  // Add/remove/reorder mutate the shared skeleton (primary). Lock them in
  // non-primary mutations — per-slot replace and text edit stay available.
  // A note above the items list explains it; buttons are just disabled.
  const isOnPrimaryFeedLang = computed(() => mjmlStore.isOnPrimaryLang());

  const supportsDynamicBlocks = computed(() => {
    return apiBlocksStore.supportsDynamicBlocks && mergetags.value.length > 0;
  });

  const mode = computed({
    get() {
      return props.block.mode || "static";
    },
    set(val: string) {
      mjmlStore.updateBlockProp({
        prop: "mode",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
        skipSnapshot: true,
      });
    },
  });

  const mergetags = computed(() => {
    if (!apiBlocksStore.supportsDynamicBlocks) {
      return [];
    }
    if (blockProperties.value && blockProperties.value.dynamicMergetags) {
      return blockProperties.value.dynamicMergetags;
    }
    return [];
  });

  const forMergetag = computed({
    get() {
      if (props.block.forMergeTag) {
        return (
          mergetags.value.find((m) => m.value === props.block.forMergeTag) ||
          props.block.forMergeTag
        );
      }
      return null;
    },
    set(val: string) {
      mjmlStore.updateBlockProp({
        prop: "forMergeTag",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const maxPerRow = computed({
    get() {
      return props.block.maxPerRow;
    },
    set(val: number) {
      if (val < 1) {
        val = 1;
      }
      if (val > 4) {
        val = 4;
      }
      mjmlStore.updateBlockProp({
        prop: "maxPerRow",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const blockProperties = computed(() => {
    return apiBlocksStore.blocks.get(typeName.value);
  });

  const pluralName = computed(
    () => blockProperties.value?.pluralName ?? t("api_block.items")
  );

  // Concatenating "apply to all" + the plural noun only reads correctly in
  // languages that don't inflect it. Locales that need a different case (e.g.
  // Polish genitive "produktów") can override the whole sentence per block type.
  // te() is pinned to the current locale on purpose: without it the check falls
  // back to en and every locale would inherit an English override.
  const applyToAllLabel = computed(() => {
    const overrideKey = `api_block.apply_to_all_${typeName.value}`;
    return te(overrideKey, locale.value)
      ? t(overrideKey)
      : `${t("api_block.apply_to_all")} ${pluralName.value.toLowerCase()}`;
  });

  const layout = computed(() => {
    const blocks = props.block.attributes["layout"].split(",");

    const layout = [];

    for (const i in blocks) {
      const blockParts = blocks[i].split(":");

      layout.push({
        type: blockParts[0],
        displayed: blockParts[1],
        uid: shortid.generate(),
      });
    }

    return layout;
  });

  const prodstructure = computed(() => {
    if (props.block.attributes["prodstructure"]) {
      return props.block.attributes["prodstructure"];
    }
    return false;
  });

  const itemsLayout = computed({
    get() {
      return props.block.layout || "column";
    },
    set(val: string) {
      mjmlStore.updateBlockProp({
        prop: "layout",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const valign = computed({
    get() {
      if (itemsLayout.value === "column") {
        const columnVerticalAlign =
          props.block.children?.[0]?.attributes?.["vertical-align"];
        if (columnVerticalAlign) {
          return columnVerticalAlign;
        }
        return "top";
      } else {
        if (mjmlStore.getActiveBlockAttrString("vertical-align")) {
          return mjmlStore.getActiveBlockAttrString("vertical-align");
        }
      }
      return "middle";
    },
    set(val: string) {
      if (itemsLayout.value === "column") {
        mjmlStore.updateSpecificBlockColumnAttribute({
          columnIndex: 0,
          prop: "vertical-align",
          value: val,
        });
      } else {
        mjmlStore.updateBlockAttribute({
          prop: "vertical-align",
          value: val,
          product: true,
          prodstructure: prodstructure.value,
        });
      }
    },
  });

  const doNotStuck = computed({
    get() {
      if (mjmlStore.getActiveBlockAttrString("in-group")) {
        return mjmlStore.getActiveBlockAttrString("in-group");
      }
      return false;
    },
    set(val: boolean) {
      mjmlStore.updateBlockAttribute({
        prop: "in-group",
        value: val,
        product: true,
        prodstructure: prodstructure.value,
      });
    },
  });

  const stuckOnMobile = computed({
    get() {
      return !doNotStuck.value;
    },
    set(val: boolean) {
      doNotStuck.value = !val;
    },
  });

  function selectForMergetag(option: TDropdownOption) {
    forMergetag.value = option.value as string;
  }

  const debounceFeedLoad = useDebounceFn(async function (feedQuery) {
    apiBlock.feedsData.value = [];

    const doc = new DOMParser().parseFromString(feedQuery, "text/html");

    const strippedFeedQuery = doc.body.textContent || "";

    await apiBlock.getFeeds(
      strippedFeedQuery,
      undefined,
      getFeedsCurrentPage()
    );
  }, 300);

  const debounceProductLoad = useDebounceFn(async function (productQuery) {
    apiBlock.itemsData.value = [];

    const doc = new DOMParser().parseFromString(productQuery, "text/html");

    const strippedProductQuery = doc.body.textContent || "";

    await apiBlock.getItems(
      undefined,
      strippedProductQuery,
      getProductCurrentPage()
    );
  }, 300);

  function onModeTabChanged(tab: number) {
    mjmlStore.resetFeedItems();
    if (tab == 0) {
      mode.value = "static";
    } else {
      mode.value = "dynamic";
      mjmlStore.addApiBlockDynamicItem({
        typeName: typeName.value,
      });
    }
  }

  function getLabel(name: string) {
    if (typeName.value === "product") {
      if (name === "priceBefore") {
        name = "price-before";
      }
    }
    if (typeName.value === "article") {
      if (name === "publishedat") {
        name = "published-at";
      }
      if (name === "publishedby") {
        name = "published-by";
      }
    }
    if (blockProperties.value && blockProperties.value.blockStructure[name]) {
      return blockProperties.value.blockStructure[name].label;
    }
    return "";
  }

  function onUpdate(evt: any) {
    if (evt.newIndex >= layout.value.length) {
      let k = evt.newIndex - layout.value.length;
      while (k-- + 1) {
        //@ts-expect-error
        layout.value.push(undefined);
      }
    }
    layout.value.splice(
      evt.newIndex,
      0,
      layout.value.splice(evt.oldIndex, 1)[0]
    );

    const sendBlocks = [];
    for (const i in layout.value) {
      sendBlocks.push(layout.value[i].type + ":" + layout.value[i].displayed);
    }
    mjmlStore.updateBlockAttribute({
      prop: "layout",
      value: sendBlocks.join(","),
      product: true,
      prodstructure: prodstructure.value,
    });
  }

  function getProductCurrentPage() {
    if (apiBlock.items.value && apiBlock.items.value.current_page) {
      return apiBlock.items.value.current_page;
    }

    return 1;
  }

  function getFeedsCurrentPage() {
    if (apiBlock.feeds.value && apiBlock.feeds.value.current_page) {
      return apiBlock.feeds.value.current_page;
    }

    return 1;
  }

  async function handleSelectFeed(feed: FeedData) {
    apiBlock.activeFeed.value = feed.id;
    if (feed.name) {
      apiBlock.selectedFeed.value = feed;
      apiBlock.feedQuery.value = feed.name;
    }

    //reset item query without watch trigger
    apiBlock.isInitItem.value = true;
    apiBlock.itemQuery.value = "";
    apiBlock.selectedItem.value = undefined;

    apiBlock.itemsData.value = [];
    apiBlock.items.value = null;
    isInEuro.value = false;

    await apiBlock.getItems(undefined, undefined, getProductCurrentPage());
  }

  function removeItem(index: number) {
    mjmlStore.removeFeedItem({ index });
  }

  function openReplace(index: number) {
    replaceIndex.value = index;
    showSelection.value = true;
  }

  function closeSelection() {
    showSelection.value = false;
    replaceIndex.value = null;
  }

  function replaceItemAt(index: number, picked: ProductData[]) {
    const newItem = picked[0];
    if (!newItem) return;

    mjmlStore.replaceFeedItemAt({
      index,
      item: newItem,
      typeName: typeName.value,
    });

    isInEuro.value = items.value.some((item: any) => item.currency === "EUR");
  }

  function updateItemField(index: number, field: string, value: string) {
    mjmlStore.updateFeedItemField({ index, field, value });
  }

  function selectionUpdate(items: ProductData[]) {
    mjmlStore.selectApiItems({
      items: items,
      typeName: typeName.value,
    });

    if (items.some((item) => item.currency === "EUR")) {
      isInEuro.value = true;
    } else {
      isInEuro.value = false;
    }

    if (
      items.some(
        (item) =>
          item.price_before !== undefined &&
          (item.price_before === "" || item.price_before === null)
      )
    ) {
      hideBlock("priceBefore", true);
    }
  }

  function showBlock(pblock: string) {
    const sendBlocks = [];
    for (const i in layout.value) {
      if (layout.value[i].type == pblock) {
        layout.value[i].displayed = "show";
      }
      sendBlocks.push(layout.value[i].type + ":" + layout.value[i].displayed);
    }
    mjmlStore.updateBlockAttribute({
      prop: "layout",
      value: sendBlocks.join(","),
      product: true,
      prodstructure: prodstructure.value,
    });
  }

  function hideBlock(pblock: string, skipSnapshot = false) {
    const sendBlocks = [];
    for (const i in layout.value) {
      if (layout.value[i].type == pblock) {
        layout.value[i].displayed = "hide";
      }
      sendBlocks.push(layout.value[i].type + ":" + layout.value[i].displayed);
    }
    mjmlStore.updateBlockAttribute({
      prop: "layout",
      value: sendBlocks.join(","),
      product: true,
      prodstructure: prodstructure.value,
      skipSnapshot: skipSnapshot,
    });
  }

  async function getInitData() {
    const itemId = props.block.attributes[`${typeName.value}_id`];
    const itemFeedId = props.block.attributes[`${typeName.value}_feed_id`];

    if (itemId !== undefined && itemFeedId !== undefined) {
      apiBlock.activeFeed.value = itemFeedId;

      //We tell watcher to not trigger additional debounced load because data are already fetched
      //We set the isInitProduct to false after wards to enable debounced searching again
      //This is a workaround to not trigger watcher for certain action
      apiBlock.isInitItem.value = true;
      apiBlock.isInitFeed.value = true;

      //get init products
      await apiBlock.getItems(itemId, "", getProductCurrentPage());

      const item = apiBlock.itemsData.value.find((p) => p.id === itemId);

      if (item) {
        apiBlock.itemQuery.value = item.name;
        apiBlock.selectedItem.value = item;
      } else {
        notificationsStore.createNotification({
          type: "info",
          title: "Zobrazený produkt nebyl v produktovém feedu nalezen.",
          text: "",
        });

        apiBlock.feedsData.value = [];
        apiBlock.itemsData.value = [];
        apiBlock.items.value = null;
        apiBlock.feeds.value = null;
        apiBlock.itemQuery.value = "";
        apiBlock.selectedItem.value = undefined;
        apiBlock.feedQuery.value = "";
        apiBlock.selectedFeed.value = undefined;

        await apiBlock.getFeeds(undefined, undefined, getFeedsCurrentPage());

        return;
      }

      //get init feeds
      await apiBlock.getFeeds(
        "",
        apiBlock.activeFeed.value as string,
        getFeedsCurrentPage()
      );

      const feed = apiBlock.feedsData.value.find((f) => f.id === itemFeedId);

      if (feed) {
        apiBlock.feedQuery.value = feed.name;
        apiBlock.selectedFeed.value = feed;
      } else {
        apiBlock.feedsData.value = [];
        apiBlock.itemsData.value = [];
        apiBlock.items.value = null;
        apiBlock.feeds.value = null;
        apiBlock.itemQuery.value = "";
        apiBlock.selectedItem.value = undefined;
        apiBlock.feedQuery.value = "";
        apiBlock.selectedFeed.value = undefined;

        notificationsStore.createNotification({
          type: "info",
          text: "Produktový feed zobrazeného produktu již neexistuje, zvolte prosím jiný.",
        });

        await apiBlock.getFeeds(undefined, undefined, getFeedsCurrentPage());

        return;
      }
    } else {
      await apiBlock.getFeeds(undefined, undefined, getFeedsCurrentPage());

      if (apiBlock.feedsData.value.length === 1) {
        handleSelectFeed(apiBlock.feedsData.value[0]);
      }
    }
  }

  onUpdated(() => {
    if (uid.value != props.block.uid) {
      uid.value = props.block.uid;
      if (
        props.block.attributes["title"] &&
        props.block.attributes["title"] != "*|PRODUCT_TITLE|*"
      ) {
        if (props.block.attributes["title"] != apiBlock.itemQuery.value) {
          apiBlock.itemQuery.value = props.block.attributes["title"];
        }
      } else if (
        props.block.attributes["title"] &&
        props.block.attributes["title"] == "*|PRODUCT_TITLE|*"
      ) {
        apiBlock.itemQuery.value = undefined;
      }
    }
  });

  watch([apiBlock.feedQuery, apiBlock.itemQuery], async (newQ, oldQ) => {
    if (!apiBlock.isInitFeed.value) {
      if (newQ[0] !== oldQ[0]) {
        if (apiBlock.feeds.value) {
          apiBlock.feeds.value.current_page = 1;
          debounceFeedLoad(newQ[0]);
        }
      }
    }

    apiBlock.isInitFeed.value = false;

    if (!apiBlock.isInitItem.value) {
      if (newQ[1] !== oldQ[1]) {
        if (apiBlock.items.value !== null) {
          apiBlock.items.value.current_page = 1;
        }
        debounceProductLoad(newQ[1]);
      }
    }

    apiBlock.isInitItem.value = false;
  });

  const forceOpenPicker = ref(false);

  function handleOpenFeedPicker(payload: unknown) {
    const data = payload as { uid?: string } | undefined;
    if (data?.uid === props.block.uid && mode.value === "static") {
      forceOpenPicker.value = true;
      showSelection.value = true;
    }
  }

  onMounted(async () => {
    uid.value = props.block.uid;
    window.$event.on("OPEN_FEED_PICKER", handleOpenFeedPicker);

    await getInitData();
    if (!supportsDynamicBlocks.value && mode.value === "dynamic") {
      onModeTabChanged(0);
    }
    if (forceOpenPicker.value && mode.value === "static") {
      showSelection.value = true;
    } else if (
      items.value.length > 0 ||
      props.block.mode === "dynamic" ||
      mergetags.value.length > 0
    ) {
      showSelection.value = false;
    } else {
      showSelection.value = true;
    }
  });

  onBeforeUnmount(() => {
    window.$event.off("OPEN_FEED_PICKER", handleOpenFeedPicker);
  });
</script>
